export type TagsTranslation = {
  aliases: {
    add: string;
    delete: string;
    duplicateError: string;
    empty: string;
    error: string;
    field: string;
    info: string;
    notFoundError: string;
    title: string;
    tooLong: string;
    validation: string;
  };
  body: {
    actions: string;
    category: string;
    group: string;
    id: string;
    name: string;
    usage: string;
  };
  bulk: {
    addToGroup: string;
    addToGroupInfo: string;
    addToGroupTitle: string;
    clear: string;
    delete: string;
    deleteConfirmation: string;
    merge: string;
    mixedTypes: string;
    noneInGroup: string;
    removeFromGroup: string;
    selected: string;
  };
  deleteImpact: {
    members: string;
    mergeHint: string;
    question: string;
    usage: string;
  };
  dialog: {
    buttons: {
      cancel: string;
      create: string;
      save: string;
    };
    createTitle: string;
    editTitle: string;
    fields: {
      category: string;
      name: string;
    };
    validation: {
      categoryRequired: string;
      nameRequired: string;
      nameTooLong: string;
    };
  };
  drawer: {
    actionsSection: string;
    addMembers: string;
    addMembersHint: string;
    candidates: string;
    candidatesWithGroup: string;
    close: string;
    delete: string;
    discardGroup: string;
    groupInfo: string;
    groupInfoDetails: string;
    groupSection: string;
    joinGroup: string;
    joinGroupSave: string;
    leaveGroup: string;
    memberOf: string;
    members: string;
    merge: string;
    moveSave: string;
    movesFrom: string;
    moveTo: string;
    nameSection: string;
    noMembers: string;
    pendingAdd: string;
    removeMember: string;
    rename: string;
    requestFailed: string;
    saveFailed: string;
    saveGroup: string;
    undoRemove: string;
  };
  groupBadge: string;
  groupDialog: {
    buttons: {
      cancel: string;
      save: string;
    };
    field: string;
  };
  groupRoles: {
    MEMBER: string;
    PARENT: string;
    UNGROUPED: string;
    UNUSED: string;
  };
  head: {
    category: string;
    groupRole: string;
    searchPhrase: string;
  };
  labels: {
    aliases: string;
    delete: string;
    merge: string;
  };
  mergeDialog: {
    buttons: {
      cancel: string;
      merge: string;
    };
    fields: {
      sourceTags: string;
      targetTag: string;
    };
    info: string;
    loadingText: string;
    noOptionsText: string;
    title: string;
    validation: {
      sourceTagsRequired: string;
      targetTagRequired: string;
    };
  };
  quickAdd: {
    add: string;
    notFound: string;
  };
  row: {
    more: string;
    openDetails: string;
    select: string;
    showMembers: string;
    usageInGroup: string;
  };
  tooltip: {
    addTag: string;
  };
};

export const tagsTranslation: TagsTranslation = {
  aliases: {
    add: 'Add alias',
    delete: 'Delete alias',
    duplicateError: 'This alias already exists.',
    empty: 'No aliases assigned.',
    error: 'Could not update aliases.',
    field: 'Alias',
    info: 'Aliases help users find {{value}}. They do not create a separate keyword.',
    notFoundError: 'The keyword or alias no longer exists.',
    title: 'Aliases for {{value}}',
    tooLong: 'Alias cannot exceed 60 characters.',
    validation: 'Enter an alias.',
  },
  body: {
    actions: 'Actions',
    category: 'Type',
    group: 'Group',
    id: 'ID',
    name: 'Name',
    usage: 'Candidates',
  },
  bulk: {
    addToGroup: 'Add to group…',
    addToGroupInfo:
      'Pick the group keyword. Selected keywords that already belong to another group will be moved.',
    addToGroupTitle: 'Add {{count}} selected to a group',
    clear: 'Clear selection',
    delete: 'Delete',
    deleteConfirmation:
      'Delete {{count}} selected keywords? Candidates lose them ({{usage}} assignments in total) and their group members become standalone.',
    merge: 'Merge…',
    mixedTypes: 'Select keywords of one type to group or merge them.',
    noneInGroup: 'None of the selected keywords is in a group.',
    removeFromGroup: 'Remove from group',
    selected: 'Selected: {{count}}',
  },
  deleteImpact: {
    members: 'Group members ({{count}}) will become standalone keywords.',
    mergeHint: 'If it is a duplicate, merge it instead - that keeps the candidate assignments.',
    question: 'Delete the keyword "{{value}}"?',
    usage: '{{count}} candidates will lose this keyword.',
  },
  dialog: {
    buttons: {
      cancel: 'Cancel',
      create: 'Create',
      save: 'Save',
    },
    createTitle: 'Add Keyword',
    editTitle: 'Edit Keyword',
    fields: {
      category: 'Type',
      name: 'Name',
    },
    validation: {
      categoryRequired: 'Keyword type is required.',
      nameRequired: 'Keyword name is required.',
      nameTooLong: 'Keyword name cannot exceed 60 characters.',
    },
  },
  drawer: {
    actionsSection: 'Other actions',
    addMembers: 'Add keywords to this group',
    addMembersHint: 'Only {{category}} keywords that do not head their own group.',
    candidates: 'Candidates',
    candidatesWithGroup: '{{count}} (+{{groupCount}} through group members)',
    close: 'Close',
    delete: 'Delete keyword',
    discardGroup: 'Discard',
    groupInfo: 'Searching by a group keyword also finds candidates with any of its members.',
    groupInfoDetails:
      'Searching by a single member finds only that member. Groups have one level: members must be of the same type and cannot head their own group.',
    groupSection: 'Group',
    joinGroup: 'Or put this keyword into another group',
    joinGroupSave: 'Add to group',
    leaveGroup: 'Remove from group',
    memberOf: 'Member of the group',
    members: 'Members ({{count}})',
    merge: 'Merge into another keyword…',
    moveSave: 'Move',
    movesFrom: 'moves from {{value}}',
    moveTo: 'Move to another group',
    nameSection: 'Name',
    noMembers: 'No members yet. Add keywords that should be found together with "{{value}}".',
    pendingAdd: 'new',
    removeMember: 'Remove {{value}} from the group',
    rename: 'Save name',
    requestFailed:
      'Not saved: {{details}} Changes that are still listed were not applied - try again.',
    saveFailed: 'Not changed: {{details}}',
    saveGroup: 'Save group ({{count}} changes)',
    undoRemove: 'Undo',
  },
  groupBadge: 'group · {{count}}',
  groupDialog: {
    buttons: {
      cancel: 'Cancel',
      save: 'Save',
    },
    field: 'Group keyword',
  },
  groupRoles: {
    MEMBER: 'In a group',
    PARENT: 'Group keywords',
    UNGROUPED: 'Not in a group',
    UNUSED: 'Unused (0 candidates)',
  },
  head: {
    category: 'Type',
    groupRole: 'Group',
    searchPhrase: 'Search by name',
  },
  labels: {
    aliases: 'Aliases',
    delete: 'Delete',
    merge: 'Merge',
  },
  mergeDialog: {
    buttons: {
      cancel: 'Cancel',
      merge: 'Merge',
    },
    fields: {
      sourceTags: 'Keywords to merge',
      targetTag: 'Target keyword',
    },
    info: 'Merging replaces the selected source keywords with the target keyword. Candidate assignments and aliases are rewritten to the target keyword, and the source keywords are deleted.',
    loadingText: 'Loading keywords...',
    noOptionsText: 'No keywords found',
    title: 'Merge Keywords',
    validation: {
      sourceTagsRequired: 'Select at least one keyword to merge.',
      targetTagRequired: 'Target keyword is required.',
    },
  },
  quickAdd: {
    add: 'Add "{{value}}" as a new keyword',
    notFound: 'No keyword matches "{{value}}".',
  },
  row: {
    more: 'More actions',
    openDetails: 'Details',
    select: 'Select {{value}}',
    showMembers: 'Show group members',
    usageInGroup: '+{{count}} in group',
  },
  tooltip: {
    addTag: 'Add keyword',
  },
};
